// @flow

import type {Cancelable} from '../types/cancelable';
import {makeSimpleRequest} from '../util/ajax';
import type {ReduceTripOptions, RoadSurfaceRouteOptions} from '../ui/server_side_structs';
import {postReduceTripRequestParameters, postReduceTripReportRequestParameters, postRoadSurfaceRouteRequestParameters} from '../ui/server_side_structs';

/**
 * Routing
 */
export const Routing = {
    // I would like to refoctor code by adding post route path and report methods here later.

    /**
     * Derived route path using POST
     * @param {ReduceTripOptions} Options
     * @returns {Cancelable} Cancelable request
     *
     * @example
     * const options = {
     *     routePings: [
     *         new TrimbleMaps.LngLat(-118.309434, 33.714154),
     *         new TrimbleMaps.LngLat(-118.316631, 33.721061)
     *     ],
     *     offRouteMiles: 0,
     *     highwayOnly: false,
     *     reportType: TrimbleMaps.Common.ReportType.MILEAGE, // or TrimbleMaps.Common.ReportType.DETAIL
     *     region: TrimbleMaps.Common.Region.NA,
     *     routingOptions: {
     *         vehicleType: TrimbleMaps.Common.VehicleType.TRUCK,
     *         routeType: TrimbleMaps.Common.RouteType.PRACTICAL,
     *         routeOptimization: TrimbleMaps.Common.RouteOptimization.NONE,
     *         tollDiscourage: false,
     *         afSetIDs: [24, 133, 204],
     *         afSetNames: ['Highways', 'TollRoads', 'Malls'],
     *         bordersOpen: true,
     *         overrideRestrict: false,
     *         highwayOnly: false,
     *         hazMatTypes: [TrimbleMaps.Common.HazMatType.NONE],
     *         distanceUnits: TrimbleMaps.Common.DistanceUnit.MILES,
     *         trkUnits: TrimbleMaps.Common.TruckUnit.ENGLISH,
     *         trkHeight: '13\u00276\"',
     *         trkLength: '48\u00270\"',
     *         trkWidth: '96\"',
     *         trkWeight: '80000',
     *         trkAxles: 5,
     *         useSites: false,
     *         overrideClass: TrimbleMaps.Common.ClassOverride.NONE
     *     },
     *     extendedOptions: {
     *         estimatedTimeOptions: {
     *             eTAETDType: TrimbleMaps.Common.ETAETDType.DEPART,
     *             dateOption: TrimbleMaps.Common.DateOption.SPECIFIC,
     *             dateAndTime: {
     *                 calendarDate: '4/23/2014',
     *                 dayOfWeek: TrimbleMaps.Common.DayOfWeek.SUNDAY,
     *                 timeOfDay: '6:00 AM',
     *                 timeZone: TrimbleMaps.Common.TimeZone.LOCAL
     *             }
     *         },
     *         useTraffic: true,
     *         truckStyle: TrimbleMaps.Common.TruckConfig.NONE,
     *         includeVehicleRestrictedCleanupPoints: false
     *     },
     *     callback: function(error, response) {
     *         if(error === null) {
     *             console.log('success', response);
     *         } else {
     *             console.log('failure', error);
     *         }
     *     }
     * };
     * const derivedRoutePath = TrimbleMaps.Routing.postDerivedRoutePath(options);
     */
    postDerivedRoutePath,

    /**
     * Derived route path reports (mileage or detail) using POST
     * @param {ReduceTripOptions} Options
     * @returns {Cancelable} Cancelable request
     *
     * @example
     * const options = {
     *     routePings: [
     *         new TrimbleMaps.LngLat(-118.309434, 33.714154),
     *         new TrimbleMaps.LngLat(-118.316631, 33.721061)
     *     ],
     *     offRouteMiles: 0,
     *     highwayOnly: false,
     *     reportType: TrimbleMaps.Common.ReportType.MILEAGE, // or TrimbleMaps.Common.ReportType.DETAIL
     *     region: TrimbleMaps.Common.Region.NA,
     *     routingOptions: {
     *         vehicleType: TrimbleMaps.Common.VehicleType.TRUCK,
     *         routeType: TrimbleMaps.Common.RouteType.PRACTICAL,
     *         routeOptimization: TrimbleMaps.Common.RouteOptimization.NONE,
     *         tollDiscourage: false,
     *         afSetIDs: [24, 133, 204],
     *         afSetNames: ['Highways', 'TollRoads', 'Malls'],
     *         bordersOpen: true,
     *         overrideRestrict: false,
     *         highwayOnly: false,
     *         hazMatTypes: [TrimbleMaps.Common.HazMatType.NONE],
     *         distanceUnits: TrimbleMaps.Common.DistanceUnit.MILES,
     *         trkUnits: TrimbleMaps.Common.TruckUnit.ENGLISH,
     *         trkHeight: '13\u00276\"',
     *         trkLength: '48\u00270\"',
     *         trkWidth: '96\"',
     *         trkWeight: '80000',
     *         trkAxles: 5,
     *         useSites: false,
     *         overrideClass: TrimbleMaps.Common.ClassOverride.NONE
     *     },
     *     extendedOptions: {
     *         estimatedTimeOptions: {
     *             eTAETDType: TrimbleMaps.Common.ETAETDType.DEPART,
     *             dateOption: TrimbleMaps.Common.DateOption.SPECIFIC,
     *             dateAndTime: {
     *                 calendarDate: '4/23/2014',
     *                 dayOfWeek: TrimbleMaps.Common.DayOfWeek.SUNDAY,
     *                 timeOfDay: '6:00 AM',
     *                 timeZone: TrimbleMaps.Common.TimeZone.LOCAL
     *             }
     *         },
     *         useTraffic: true,
     *         truckStyle: TrimbleMaps.Common.TruckConfig.NONE,
     *         includeVehicleRestrictedCleanupPoints: false
     *     },
     *     callback: function(error, response) {
     *         if (error === null) {
     *             console.log('success', response);
     *         } else {
     *             console.log('failure', error);
     *         }
     *     }
     * };
     * const derivedRouteReports = TrimbleMaps.Routing.postDerivedRouteReports(options);
     */
    postDerivedRouteReports,

    /**
     * POST request retrieves a list of road surface conditions at each point of a route path. The conditions are returned in an integer array where
     * 1 - Dry road
     * 2 - Wet road
     * 3 - Ice/snow
     * 4 - Mix water/snow
     * 5 - Dew
     * 6 - Melting snow
     * 7 - Frost
     * 8 - Icing rain
     * 99 - Not in range
     *
     * @param {RoadSurfaceRouteOptions} options Road surface route options
     * @returns {Cancelable} Cancelable request.
     *
     * @example
     * const options = {
     *     routeDuration: 3155,
     *     provider: 'RC1',
     *     routePath: [[-74.437916,39.362518],[-74.43799,39.36264],[-74.43803,39.362718],[-74.438961,39.362649],[-74.438942,39.362469]],
     *     callback: function(error, response) {
     *         if(error === null) {
     *             // sample response {"data":"[1,1,1,1,1]"}
     *             console.log('success', response);
     *         } else {
     *             console.log('failure', error);
     *         }
     *     }
     * };
     * const request  = TrimbleMaps.Routing.postRoadSurfaceRoute(options);
     */
    postRoadSurfaceRoute
};

function postDerivedRoutePath(options: ReduceTripOptions): Cancelable {
    const requestParameters = postReduceTripRequestParameters(options);
    const request = makeSimpleRequest('POST', requestParameters, options.callback);
    return {cancel: () => request.cancel()};
}

function postDerivedRouteReports(options: ReduceTripOptions): Cancelable {
    const requestParameters = postReduceTripReportRequestParameters(options);
    const request = makeSimpleRequest('POST', requestParameters, options.callback);
    return {cancel: () => request.cancel()};
}

function postRoadSurfaceRoute (options: RoadSurfaceRouteOptions): Cancelable {
    const requestParameters = postRoadSurfaceRouteRequestParameters(options);
    const request = makeSimpleRequest('POST', requestParameters, options.callback);
    return {cancel: () => request.cancel()};
}

